import { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../helpers'

const josefInputVariants = cva(
  'w-full rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 autofill:shadow-[inset_0_0_0px_1000px] autofill:[-webkit-text-fill-color:inherit]',
  {
    variants: {
      variant: {
        default: [
          'bg-white dark:bg-black',
          'border-black/20 dark:border-white/20',
          'text-black dark:text-white',
          'placeholder:text-black/60 dark:placeholder:text-white/60',
          'focus:border-black/40 dark:focus:border-white/40',
          'focus:ring-black/20 dark:focus:ring-white/20',
          'autofill:shadow-[inset_0_0_0px_1000px_white] dark:autofill:shadow-[inset_0_0_0px_1000px_black]',
          'autofill:[-webkit-text-fill-color:black] dark:autofill:[-webkit-text-fill-color:white]',
        ],
        ghost: [
          'bg-transparent',
          'border-black/10 dark:border-white/10',
          'text-black dark:text-white',
          'placeholder:text-black/60 dark:placeholder:text-white/60',
          'hover:bg-black/5 dark:hover:bg-white/5',
          'focus:border-black/20 dark:focus:border-white/20',
          'focus:ring-black/10 dark:focus:ring-white/10',
          'autofill:shadow-[inset_0_0_0px_1000px_transparent]',
          'autofill:[-webkit-text-fill-color:black] dark:autofill:[-webkit-text-fill-color:white]',
        ],
        borderless: [
          'bg-white dark:bg-black',
          'border-transparent',
          'text-black dark:text-white',
          'placeholder:text-black/60 dark:placeholder:text-white/60',
          'focus:border-transparent',
          'focus:ring-0',
          'autofill:shadow-[inset_0_0_0px_1000px_white] dark:autofill:shadow-[inset_0_0_0px_1000px_black]',
          'autofill:[-webkit-text-fill-color:black] dark:autofill:[-webkit-text-fill-color:white]',
        ],
      },
      size: {
        sm: 'h-8 px-3 py-1 text-xs',
        md: 'h-10 px-3 py-2 text-sm',
        lg: 'h-12 px-4 py-3 text-base',
      },
    },
    defaultVariants: {
      variant: 'borderless',
      size: 'md',
    },
  }
)

export interface JosefInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof josefInputVariants> {
  error?: string
  label?: string
}

const JosefInput = forwardRef<HTMLInputElement, JosefInputProps>(
  ({ className, variant, size, error, label, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-black dark:text-white mb-2">
            {label}
          </label>
        )}
        <input
          className={cn(josefInputVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    )
  }
)

JosefInput.displayName = 'JosefInput'

export { JosefInput, josefInputVariants }
export default JosefInput
